import React, { useRef, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  IonPage,
  IonContent,
  IonMenuButton,
  IonIcon
} from "@ionic/react";
import { chevronForwardOutline } from "ionicons/icons";
import { sendStartAction, getInvalidDomesticGliderDocumentChecks } from "../common/ApiUtils";
import { ActiveSelect } from "../common/constants";
import { reverseDateToLocalDate, fromHumanToLocalDate } from "../common/TimeUtils";
import {
  filterGlider,
  filterIncomplete,
  filterPilot,
  filterTowingPilot,
  filterTowingPlane,
  onCloseReceiveFalse,
  gliderDocInvaliditySet,
  gliderDocInvalidityDismiss
} from "../reduce/actions";
import { stateData } from "../store/stateData";
import { PlaneBookDayItems } from "./PlaneBookDayItems";
import AeroTowList from "../aerotow/AeroTowList";
import AeroTowDetail from "../aerotow/AeroTowDetail";
import GliderTaskCompose from "../task/GliderTaskCompose";
import TowTaskCompose from "../towtask/TowTaskCompose";
import "./FlyDay.css";

// Hooks
import { useApiErrorHandler } from "./hooks/useApiErrorHandler";
import { useToast } from "./hooks/useToast";
import { useNavigation } from "./hooks/useNavigation";
import { useStompSubscription } from "./hooks/useStompSubscription";
import { useFlyDayData } from "./hooks/useFlyDayData";
import { useActiveConfiguration } from "./hooks/useActiveConfiguration";
import { useTowManagement } from "./hooks/useTowManagement";

// Components
import { ErrorAlert, ToastAlert } from "./components/ErrorAlerts";
import { FlyDayHeader } from "./components/FlyDayHeader";
import { FlyDayFooter } from "./components/FlyDayFooter";
import { ActiveDetailView } from "./components/ActiveDetailView";
import { PilotSelectionView } from "./components/PilotSelectionView";
import { GliderSelectionView } from "./components/GliderSelectionView";
import { TowingPlaneSelectionView } from "./components/TowingPlaneSelectionView";
import { GliderDocumentInvalidityOverlay } from "./components/GliderDocumentInvalidityOverlay";

// Utils
import { convertDateMinusToDot } from "./utils/flyDayUtils";

const FlyDay = () => {
  const { dateParam } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux selectors
  const onCloseMessage = useSelector(state => state?.onCloseMessage);
  const onCloseMessageChange = useSelector(state => state?.onCloseMessageChange);
  const filterPilotSelector = useSelector(state => state?.filterPilot);
  const filterGliderSelector = useSelector(state => state?.filterGlider);
  const filterTowingPlaneSelector = useSelector(state => state?.filterTowingPlane);
  const filterTowingPilotSelector = useSelector(state => state?.filterTowingPilot);
  const canDownloadPdf = useSelector(state => state?.canDownloadPdf);
  const day = useSelector(state => state?.day || "");
  const active = useSelector(state => state?.active || {});
  const incompleteFilter = useSelector(state => state?.incompleteFilter || false);
  const reduxFlyDay = useSelector(state => state?.flyDay);
  const flyDayStompConnectionStatus = useSelector(state => state?.flyDayStompConnectionStatus || false);
  const gliderDocInvalidity = useSelector(
    (state) => state?.gliderDocInvalidity ?? stateData.gliderDocInvalidity
  );
  const gliderDocInvalidityInitialized = useSelector(
    (state) => state?.gliderDocInvalidity?.initialized === true
  );

  // Custom hooks
  const { showToast, toastMessage, setToast } = useToast();
  const apiErrorHandler = useApiErrorHandler(dispatch);
  const {
    type,
    detailUUID,
    editTow,
    lastPositionY,
    setNavigationType,
    setDetailUUID,
    setEditTow,
    setLastPositionY,
    selectHome
  } = useNavigation();

  const contentRef = useRef(null);

  // Data management
  const { flyDay, setFlyDay, channelConnected, setChannelConnected, getFlyDay } = useFlyDayData(
    dateParam,
    navigate
  );

  // STOMP subscription
  useStompSubscription(convertDateMinusToDot(dateParam));

  // Navigation handlers
  const handleSelectHome = useCallback(() => {
    // Pass contentRef - selectHome will handle it
    // lastPositionY might not be updated yet, but selectHome uses ref as fallback
    selectHome(contentRef, lastPositionY);
    // Don't reset lastPositionY immediately - let it be used for scroll restoration
    // It will be reset after scroll completes or next time we navigate away
  }, [selectHome, lastPositionY, contentRef]);

  const handleSelectActiveDetail = useCallback(() => {
    setNavigationType(ActiveSelect.ACTIVE_DETAIL);
  }, [setNavigationType]);

  const handleSelectTowDetail = useCallback(() => {
    setNavigationType(ActiveSelect.TOW_DETAIL);
  }, [setNavigationType]);

  const handleShowDetail = useCallback((uuid) => {
    setDetailUUID(uuid);
    handleSelectTowDetail();
  }, [setDetailUUID, handleSelectTowDetail]);

  // Active configuration
  const activeConfig = useActiveConfiguration(active, dispatch, handleSelectActiveDetail);
  const { setActive, setGlider, setTowingPlane, toggleReel } = activeConfig;

  const handleSetPilot = useCallback((pilot) => {
    if (type === ActiveSelect.FIRST) {
      activeConfig.setPilot(pilot, 'first');
    } else if (type === ActiveSelect.SECOND) {
      activeConfig.setPilot(pilot, 'second');
    } else if (type === ActiveSelect.LIFTER) {
      activeConfig.setPilot(pilot, 'lifter');
    }
  }, [type, activeConfig]);

  // Tow management
  const {
    timeChange,
    changeTow,
    gliderTaskChange,
    towTaskChange,
    setGliderTask,
    setTowTask
  } = useTowManagement(
    flyDay,
    apiErrorHandler,
    setEditTow,
    setNavigationType,
    setLastPositionY,
    handleSelectHome,
    setActive,
    setFlyDay
  );

  // Action handlers
  const startAction = useCallback(async () => {
    const reqActive = { ...active };
    reqActive.flyDay = reverseDateToLocalDate(dateParam);

    const response = await sendStartAction(reqActive);
    if (response.code !== 0) {
      setToast(true, response.message);
    }
  }, [active, dateParam, setToast]);

  const scrollToPosition = useCallback((positionY) => {
    if (contentRef.current) {
      contentRef.current.scrollToPoint(0, positionY, 100);
    }
  }, []);

  const handleCSVDownload = useCallback(() => {
    if (!flyDay?.csv) return;

    const blob = new Blob([flyDay.csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fromHumanToLocalDate(flyDay.day) + '.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [flyDay]);

  // Error handlers
  const clearOnCloseMessage = useCallback(() => {
    dispatch({ type: 'CLOSE_RESPONSE', payload: null });
  }, [dispatch]);

  // Filter dispatch functions
  const filterPilotDispatch = useCallback((load) => dispatch(filterPilot(load)), [dispatch]);
  const filterGliderDispatch = useCallback((load) => dispatch(filterGlider(load)), [dispatch]);
  const filterTowingPlaneDispatch = useCallback((load) => dispatch(filterTowingPlane(load)), [dispatch]);
  const filterTowingPilotDispatch = useCallback((load) => dispatch(filterTowingPilot(load)), [dispatch]);
  const switchIncompleteDispatch = useCallback(() => dispatch(filterIncomplete()), [dispatch]);
  const closeReceiveChange = useCallback(() => dispatch(onCloseReceiveFalse()), [dispatch]);

  const patchFlyDayTow = useCallback((updatedTow) => {
    if (!updatedTow?.id) return;
    setFlyDay((prev) => {
      if (!prev?.tows) return prev;
      return {
        ...prev,
        tows: prev.tows.map((t) => (t.id === updatedTow.id ? { ...t, ...updatedTow } : t)),
      };
    });
  }, [setFlyDay]);

  // Render functions
  const renderDetail = () => {
    if (!flyDay?.tows) return null;

    const aerotow = flyDay.tows.find(element => element.uuid === detailUUID);
    if (!aerotow) return null;

    return (
      <AeroTowDetail
        tow={aerotow}
        active={active}
        home={handleSelectHome}
        timeChange={timeChange}
        changeTow={changeTow}
        date={dateParam}
        onError={apiErrorHandler}
        onTowPersisted={patchFlyDayTow}
        getFlyDay={getFlyDay}
      />
    );
  };

  const renderGliderTaskView = () => {
    const groupId = editTow?.first?.taskGroup?.id;
    return (
      <GliderTaskCompose
        tow={editTow}
        setTask={(task) => setGliderTask(task, editTow)}
        back={handleSelectHome}
        groupId={groupId}
      />
    );
  };

  const renderTowTaskView = () => {
    return (
      <TowTaskCompose
        tow={editTow}
        setTask={(task) => setTowTask(task, editTow)}
        active={active}
        back={handleSelectHome}
      />
    );
  };

  const renderFlyDay = () => {
    const markTowId = onCloseMessage?.error ? onCloseMessage.id : null;
    const { towingPlane, lifter, glider, first, reel, towTask } = active;
    const onlyTow = lifter && towingPlane && towTask?.onlyTow;
    const startDisabled = !((lifter && towingPlane && glider && first) || (reel && glider && first) || onlyTow);

    const handleGliderTaskChange = (tow, rowOffsetTop) => gliderTaskChange(tow, rowOffsetTop);
    const handleTowTaskChange = (tow, rowOffsetTop) => towTaskChange(tow, rowOffsetTop);

    return (
      <IonPage>
        <FlyDayHeader
          flyDay={flyDay}
          channelConnected={channelConnected}
          getFlyDay={getFlyDay}
          markTowId={markTowId}
          active={active}
          filterPilotSelector={filterPilotSelector}
          filterGliderSelector={filterGliderSelector}
          filterTowingPlaneSelector={filterTowingPlaneSelector}
          filterTowingPilotSelector={filterTowingPilotSelector}
          onFilterPilotRemove={() => filterPilotDispatch(null)}
          onFilterGliderRemove={() => filterGliderDispatch(null)}
          onFilterTowingPlaneRemove={() => filterTowingPlaneDispatch(null)}
          onFilterTowingPilotRemove={() => filterTowingPilotDispatch(null)}
          onConfigClick={handleSelectActiveDetail}
          setNavigationType={setNavigationType}
        />
        <IonContent scrollEvents ref={contentRef}>
          <IonMenuButton horizontal="start" slot="fixed" className="fab-menu">
            <IonIcon icon={chevronForwardOutline} />
          </IonMenuButton>

          {flyDay?.planeBookDayItems && (
            <PlaneBookDayItems
              items={flyDay.planeBookDayItems}
              closed={!!flyDay.closed}
              currentDay={flyDay.day}
            />
          )}
          {flyDay?.tows && (
            <AeroTowList
              aeroTowList={flyDay.tows}
              timeChange={timeChange}
              detail={handleShowDetail}
              taskChange={handleGliderTaskChange}
              towTaskChange={handleTowTaskChange}
              markId={markTowId}
              scrollToPosition={scrollToPosition}
            />
          )}
          <ErrorAlert onCloseMessage={onCloseMessage} onDismiss={clearOnCloseMessage} />
          <ToastAlert
            showToast={showToast}
            toastMessage={toastMessage}
            onDismiss={() => setToast(false, "")}
          />
        </IonContent>
        <FlyDayFooter
          canDownloadPdf={canDownloadPdf}
          day={day}
          startDisabled={startDisabled}
          onStart={startAction}
          onDownloadCSV={handleCSVDownload}
        />
        {gliderDocInvalidity.rows?.length > 0 && !gliderDocInvalidity.dismissed && (
            <GliderDocumentInvalidityOverlay rows={gliderDocInvalidity.rows} onDismiss={dismissDocInvalidityOverlay} />
          )}
      </IonPage>
    );
  };

  const renderType = () => {
    switch (type) {
      case ActiveSelect.HOME:
        return renderFlyDay();
      case ActiveSelect.ACTIVE_DETAIL:
        return (
          <ActiveDetailView
            active={active}
            incompleteFilter={incompleteFilter}
            onBack={handleSelectHome}
            onToggleReel={toggleReel}
            onToggleIncomplete={switchIncompleteDispatch}
            onSelectTowingPlane={() => setNavigationType(ActiveSelect.TOWINGPLANE)}
            onSelectLifter={() => setNavigationType(ActiveSelect.LIFTER)}
            onSelectGlider={() => setNavigationType(ActiveSelect.GLIDER)}
            onSelectFirstPilot={() => setNavigationType(ActiveSelect.FIRST)}
            onSelectSecondPilot={() => setNavigationType(ActiveSelect.SECOND)}
            onSelectTowTask={() => setNavigationType(ActiveSelect.TOW_TASK_DETAIL)}
          />
        );
      case ActiveSelect.GLIDER:
        return (
          <GliderSelectionView
            type={type}
            active={active}
            onBack={handleSelectActiveDetail}
            onSelectGlider={setGlider}
          />
        );
      case ActiveSelect.FIRST:
      case ActiveSelect.SECOND:
      case ActiveSelect.LIFTER:
        return (
          <PilotSelectionView
            type={type}
            active={active}
            onBack={handleSelectActiveDetail}
            onSelectPilot={handleSetPilot}
            onClearPilot={() => handleSetPilot(null)}
          />
        );
      case ActiveSelect.TOWINGPLANE:
        return (
          <TowingPlaneSelectionView
            type={type}
            active={active}
            onBack={handleSelectActiveDetail}
            onSelectPlane={setTowingPlane}
          />
        );
      case ActiveSelect.GLIDER_TASK_DETAIL:
        return renderGliderTaskView();
      case ActiveSelect.TOW_DETAIL:
        return renderDetail();
      case ActiveSelect.TOW_TASK_DETAIL:
        return renderTowTaskView();
      default:
        return renderFlyDay();
    }
  };

  // Effects
  useEffect(() => {
    const expectedDay = convertDateMinusToDot(dateParam);
    if (
      reduxFlyDay &&
      reduxFlyDay.day === expectedDay &&
      (!flyDay || reduxFlyDay.time !== flyDay.time)
    ) {
      setFlyDay(reduxFlyDay);
    }
  }, [reduxFlyDay, flyDay, setFlyDay, dateParam]);

  useEffect(() => {
    if (flyDayStompConnectionStatus !== null && flyDayStompConnectionStatus !== channelConnected) {
      setChannelConnected(flyDayStompConnectionStatus);
    }
  }, [flyDayStompConnectionStatus, channelConnected, setChannelConnected]);

  useEffect(() => {
    if (onCloseMessageChange) {
      if (onCloseMessage?.code === 0) {
        getFlyDay();
      }
      handleSelectHome();
      closeReceiveChange();
    }
  }, [onCloseMessageChange, onCloseMessage, handleSelectHome, closeReceiveChange, getFlyDay]);

  const dismissDocInvalidityOverlay = useCallback(() => {
    dispatch(gliderDocInvalidityDismiss());
  }, [dispatch]);

  useEffect(() => {
    if (gliderDocInvalidityInitialized) {
      return;
    }
    let alive = true;
    (async () => {
      try {
        const rows = await getInvalidDomesticGliderDocumentChecks();
        if (!alive) return;
        const list = Array.isArray(rows) ? rows : [];
        dispatch(
          gliderDocInvaliditySet({
            initialized: true,
            dismissed: list.length === 0,
            rows: list
          })
        );
      } catch (e) {
        if (alive) {
          console.warn("Kontrola dokumentů kluzáků selhala", e);
          dispatch(
            gliderDocInvaliditySet({
              initialized: true,
              dismissed: true,
              rows: []
            })
          );
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [gliderDocInvalidityInitialized, dispatch]);

  return renderType();
};

export default FlyDay;
